/*    
<summary>
  This functional component "AddTreatmentManagement" defines the props like initial values and functions for the child form component.
  Developer:Sahil Bharti, Created Date:06-January-2025
</summary>
<returns>Returns JSX</returns>
*/

import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import {
  registrationTypeOptions,
} from "../../../../constants/options.constant";
import { useStore } from "../../../../contexts/store-provider";
import { IAddTreatmentManagement } from "../../../../models/forms/IAddTreatment";
import { formatMessage } from "../../../../translations/format-message";
import AddTreatmentManagementForm from "../forms/add-edit-treatment-form";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";

interface IProps {
  id: number;
  modalClosed: () => void;
}

const AddTreatmentManagement = (props: IProps) => {
  const {
    treatmentManagementStore,
    masterManagementStore,
    categoryManagementStore,
  } = useStore();
  const {
    GetTreatmentManagementByIdService,
    resetAddUpdateTreatmentManagementState,
    resetTreatmentManagementDetail,
    treatmentState,
    treatmentDetail,
    addUpdateTreatmentManagementState,
    AddTreatmentManagementService,
    UpdateTreatmentManagementService,
    deleteTreatmentManagementState,
    DeleteTreatmentManagementService,
    resetDeleteTreatmentManagement,
  } = treatmentManagementStore;
  const {
    resetCategoryList,
    allCategoryManagement,
    GetCategoryManagementListFilterService,
    inProgress,
  } = categoryManagementStore;
  const {
    GetAllTimeService,
    getAllTaxOptions,
    getAllTimeOptions,
    GetAllTaxOptionService,
    getAllBusinessTypes,
    GetAllBusinessTypeService,
  } = masterManagementStore;

  const initialValues: IAddTreatmentManagement = treatmentDetail;
  const userType = getUserTypeFromLocalStorage();
  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllBusinessTypeService();
    let userType = getUserTypeFromLocalStorage();
    if (userTypeEnum[userTypeEnum.SuperAdmin] !== userType) {
      GetAllTaxOptionService();
      GetAllTimeService();
    }
    if (props.id > 0) {
      GetTreatmentManagementByIdService(props.id);
      //  GetCategoryManagementListFilterService(initialValues?.BusinessTypeId, 1);
    }
  }, []);

  useEffect(() => {
    let userType = getUserTypeFromLocalStorage();
    if (
      userTypeEnum[userTypeEnum.SuperAdmin] === userType &&
      initialValues &&
      Number(initialValues.BusinessTypeId) > 0
    ) {
      GetAllTaxOptionService(initialValues.BusinessTypeId);
      GetAllTimeService(initialValues.BusinessTypeId);
    }
  }, [GetAllTaxOptionService, GetAllTimeService, initialValues]);
  

  // useEffect(() => {
  //   if (props.id > 0) {
  //     GetCategoryManagementListFilterService(initialValues?.BusinessTypeId, 1);
  //   }
  // }, [initialValues]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateTreatmentManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateTreatmentManagementState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateTreatmentManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "treatmentDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (treatmentState.error) {
      toast.error(formatMessage(treatmentState.error));
    }
  }, [treatmentState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateTreatmentManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateTreatmentManagementState.error) {
      toast.error(formatMessage(addUpdateTreatmentManagementState.error));
      resetAddUpdateTreatmentManagementState();
    }
  }, [addUpdateTreatmentManagementState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "deleteTreatmentManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteTreatmentManagementState.success) {
      toast.success(formatMessage("deleted_success"));
      onDeleteModalClosed();
    }
  }, [deleteTreatmentManagementState.success]);

  useEffect(() => {
    if (deleteTreatmentManagementState.error) {
      toast.error(formatMessage(deleteTreatmentManagementState.error));
      resetDeleteTreatmentManagement();
      props.modalClosed();
    }
  }, [deleteTreatmentManagementState.error, resetDeleteTreatmentManagement]);
  

  /**
   * This function adds the treatment when "Id < 0" and updates the Existing treatment when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (values: IAddTreatmentManagement) => {
    let tempValues = cloneDeep(values);
    tempValues.TreatmentName = values.TreatmentName.trim();
    if (props.id < 0) AddTreatmentManagementService(tempValues);
    else UpdateTreatmentManagementService(props.id, {...tempValues, StoreId: initialValues.StoreId ? initialValues.StoreId : null});
  };

  /**
   * This function closes the pop up modal and reset gettreatmentdetails, addUpdate treatement observables (success, inprogress
   * and error).
   */

  const onModalClosed = () => {
    if (userType === userTypeEnum[userTypeEnum.SuperAdmin]) {
      resetCategoryList();
    }
    resetTreatmentManagementDetail();
    props.modalClosed();
  };

  const handleDeleteSubmitHandler = () => {
    DeleteTreatmentManagementService(props.id);
  };

  /**
   * This function closes the pop up modal and reset gettreatmentdetails, delete treatment observables (success, inprogress
   * and error).
   */
  const onDeleteModalClosed = () => {
    resetTreatmentManagementDetail();
    resetCategoryList();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddTreatmentManagementForm
        initialValues={initialValues}
        id={props.id}
        businessTypeOptions={getAllBusinessTypes}
        categoryOptions={allCategoryManagement}
        timeOptions={getAllTimeOptions}
        taxOptions={getAllTaxOptions}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        registrationTypeOptions={registrationTypeOptions}
        getTreatmentManagementLoading={
          treatmentState.inProgress ||
          addUpdateTreatmentManagementState.inProgress ||
          inProgress
        }
        handleDeleteSubmitHandler={handleDeleteSubmitHandler}
        deleteActionLoading={deleteTreatmentManagementState.inProgress}
        viewOnly={
          props.id > 0
            ? !isOperationPermittedRoleBase(
                ModuleType.TreatmentManagement,
                ActionType.Add
              )
            : false
        }
      />
    </React.Fragment>
  );
};

export default observer(AddTreatmentManagement);
